import React, { useEffect, useState, useCallback } from "react";
import http from "../services/http";
import { Box, Grid, Card, CardMedia, CardContent, Typography, CircularProgress, Button } from "@mui/material";
import { Artist, ApiResponse } from "../types/luna";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "../contexts/SnackbarContext";
import AdminLayout from "../components/AdminLayout";

interface ArtistListResponse {
  artists: Artist[];
}

const ArtistList: React.FC = () => {
  const { logout, token } = useAuth();
  const navigate = useNavigate();
  const { notify } = useSnackbar();
  const [artists, setArtists] = useState<Artist[]>([]);
  const [loading, setLoading] = useState(false);

  const loadArtists = useCallback(async () => {
    if (!token) {
      notify("请先登录", "error");
      navigate("/");
      return;
    }

    setLoading(true);
    try {
      const res = await http.get<ApiResponse<ArtistListResponse>>(
        `artist`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (res.data.code === "000000") {
        setArtists(res.data.data.artists);
      } else {
        notify(res.data.message || "加载失败", "error");
      }
    } catch (err: any) {
      console.error(err);
      if (err.response?.status === 401) {
        notify("登录已过期，请重新登录", "error");
        logout();
        navigate("/");
      } else {
        notify("网络错误", "error");
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate, logout, notify]);

  useEffect(() => {
    loadArtists();
  }, [loadArtists]);

  return (
    <AdminLayout
      title="艺术家"
      actions={
        <Button variant="contained" color="primary" onClick={() => navigate("/artist/create")}>
          新增艺术家
        </Button>
      }
    >
      {loading ? (
        <Box textAlign="center" mt={5}>
          <CircularProgress />
        </Box>
      ) : artists.length === 0 ? (
        <Box textAlign="center" mt={5}>
          <Typography variant="h6" color="text.secondary">
            暂无艺术家，点击右上角新增
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {artists.map((artist) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={artist.id}>
              <Card>
                <CardMedia
                  component="img"
                  height="220"
                  image={artist.avatarUrl}
                  alt={artist.name}
                />
                <CardContent>
                  <Typography variant="h6">{artist.name}</Typography>
                  {artist.bio && (
                    <Typography variant="body2" color="text.secondary">
                      {artist.bio}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </AdminLayout>
  );
};

export default ArtistList;
